import React, { useEffect, useState } from "react";
import { TextEditor } from "../../../../Components/FormElements";
import { PrimaryButton } from "../../../../Components/Buttons";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../../../Redux/Ui/ui.slice";
import { SuccessModal } from "../../../../Components/Modals";
import { Loader } from "../../../../Components/Other";
import { toast } from "react-toastify";
import { APIService } from "../../../../Service";

export default function UpsertPrivacyPolicy() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* -------------------------------
      STATE
  ------------------------------- */
  const [policyId, setPolicyId] = useState(null);
  const [html, setHtml] = useState("");

  const [errors, setErrors] = useState({ content: "" });
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  /* -------------------------------
      INIT
  ------------------------------- */
  useEffect(() => {
    dispatch(setPageTitle(["Manage Content", "Edit Privacy Policy"]));
    dispatch(setSearchEnabled(false));
    dispatch(setNavLink(["/manage-content/privacy-policy"]));

    _getContent();
  }, []);

  /* -------------------------------
      FETCH CONTENT
  ------------------------------- */
  const _getContent = async () => {
    try {
      setLoader(true);

      const res = await APIService(false, "content/list", {});

      if (res?.status_code === 200) {
        const list = res?.data || [];

        const policy = list.find((item) =>
          item.type?.toLowerCase().includes("privacy"),
        );

        if (policy) {
          setPolicyId(policy.id);
          setHtml(policy.text || "");
        }
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      console.error("Privacy fetch error:", err);
      toast.error("Failed to fetch content");
    } finally {
      setLoader(false);
    }
  };

  /* -------------------------------
      VALIDATION
  ------------------------------- */
  const validate = () => {
    let valid = true;

    const plainText = html
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    if (!plainText || plainText.length < 3) {
      setErrors({ content: "Content cannot be empty." });
      valid = false;
    } else {
      setErrors({ content: "" });
    }

    return valid;
  };

  /* -------------------------------
      SUBMIT
  ------------------------------- */
  const _submitForm = () => {
    setShowError(true);

    if (!validate()) return;

    if (!policyId) {
      toast.error("Invalid content ID");
      return;
    }

    setLoading(true);

    const payload = {
      id: policyId,
      text: html,
    };

    APIService(true, "content/update", payload)
      .then((res) => {
        if (res?.status_code === 200) {
          toast.success(res?.message);
          setSuccessModal(true);
        } else {
          toast.error(res?.message || "Update failed");
        }
      })
      .catch((err) => {
        console.error("Privacy update error:", err);
        toast.error(err?.message || "Something went wrong");
      })
      .finally(() => setLoading(false));
  };

  /* -------------------------------
      LOADER UI
  ------------------------------- */
  if (loader) return <Loader />;

  /* -------------------------------
      UI
  ------------------------------- */
  return (
    <div className="row h-90">
      <div className="col-12 d-flex flex-column flex-grow-1">
        <div className="d-flex flex-column e-glass-card bg-transparent rounded-24 p-3 flex-grow-1">

          {/* Editor */}
          <TextEditor
            value={html}
            onChange={(v) => {
              setHtml(v);
              if (showError) validate();
            }}
            error={errors.content}
            placeholder="Write Privacy Policy..."
            className={errors.content ? "has-error" : ""}
          />

          {/* Error */}
          {errors.content && (
            <p className="text-danger pt-8 fs-14 text-capitalize">{errors.content}</p>
          )}

          {/* Buttons */}
          <div className="col-lg-6 col-12">
            <div className="row row-cols-2 pt-3">
              <div className="col">
                <PrimaryButton
                  name="Cancel"
                  ClassName="rounded-pill bg-transparent border-oxford-blue py-3 text-oxford-blue w-100 fs-16 fw-500"
                  onPress={() => navigate("/manage-content/privacy-policy")}
                />
              </div>

              <div className="col">
                <PrimaryButton
                  name="Update"
                  ClassName="rounded-pill bg-oxford-blue py-3 text-white w-100 fs-16 fw-500"
                  onPress={_submitForm}
                  loading={loading}
                  disabled={loading || !policyId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        title="Successful"
        message="Privacy Policy updated successfully"
        open={successModal}
        onConfirm={() => {
          setSuccessModal(false);
          navigate("/manage-content/privacy-policy");
        }}
      />
    </div>
  );
}